import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { APP_TABS, countByTab, tabbedApps, type AppTab, type TabbedApp } from '../../pages/Apps/appTabs';
import { useAppCatalog } from '../../pages/Apps/useApps';
import { useMyApps } from '../../pages/Apps/builder/useAppBuilder';
import type { FooterTab } from './footerTab';

const TAB_LABELS: Record<AppTab, { id: string; defaultMessage: string }> = {
  all: { id: 'appsFooter.tab.all', defaultMessage: 'All' },
  builtin: { id: 'appsFooter.tab.builtin', defaultMessage: 'Built-in' },
  team: { id: 'appsFooter.tab.team', defaultMessage: 'Team' },
  personal: { id: 'appsFooter.tab.personal', defaultMessage: 'Personal' },
  shared: { id: 'appsFooter.tab.shared', defaultMessage: 'Shared with you' },
};

export interface FooterApps {
  readonly apps: readonly TabbedApp[];
  readonly tabs: readonly FooterTab[];
  readonly isLoading: boolean;
  readonly catalogFailed: boolean;
  readonly mineFailed: boolean;
}

export const useFooterApps = (enabled: boolean, builderEnabled: boolean): FooterApps => {
  const intl = useIntl();
  const catalog = useAppCatalog(enabled);
  const mine = useMyApps(enabled && builderEnabled);

  const apps = useMemo(
    () => tabbedApps(catalog.data ?? [], mine.data ?? []),
    [catalog.data, mine.data],
  );
  const tabs = useMemo(() => {
    const counts = countByTab(apps);
    return APP_TABS.map((id) => ({ id, label: intl.formatMessage(TAB_LABELS[id]), count: counts[id] }));
  }, [apps, intl]);

  return {
    apps,
    tabs,
    isLoading: catalog.isLoading || mine.isLoading,
    catalogFailed: catalog.isError,
    mineFailed: mine.isError,
  };
};
